import { MiModal } from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { Box, useTheme } from "@mui/material";
import { IPriceLevelPayload } from "../PricingMatrix.types";

export function DeactivateConfirmModal({
  open,
  selectedPriceLevel,
  closeModal,
  handleUpdatePriceLevel,
}: DeactivateConfirmModalProps) {
  const theme = useTheme();
  const {
    assignedAccounts = [],
    isActive,
    name,
    id,
    modifiedOn,
  } = selectedPriceLevel || {};

  const hasAssignedAccounts = assignedAccounts.length > 0 && isActive;

  const description = hasAssignedAccounts ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Box>
        This price level has associated customers. To deactivate it, you must
        reassign those customers to a different price level.
      </Box>
      <Box>
        Tip: Hover over the info icon in the price level header to view
        associated customers.
      </Box>
    </Box>
  ) : (
    <>
      Are you sure you want to {isActive ? "deactivate" : "reactivate"}{" "}
      <span
        style={{
          fontWeight: theme.fontWeight.semiBold,
          color: theme.palette.primary.main,
        }}
      >
        {name}
      </span>{" "}
      price level?
    </>
  );

  const actions = hasAssignedAccounts
    ? [
        {
          name: "Got it",
          variant: BUTTON_VARIANTS.CONTAINED,
          onClick: closeModal,
        },
      ]
    : [
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: closeModal,
        },
        {
          name: "Yes",
          variant: BUTTON_VARIANTS.CONTAINED,
          color: BUTTON_COLORS.PRIMARY,
          onClick: () => {
            const data = { modifiedOn, id, isActive: !isActive };
            const successText = `Price Level ${
              isActive ? "Deactivated" : "Reactivated"
            }: ${name}`;
            handleUpdatePriceLevel(data, successText);
            closeModal();
          },
        },
      ];

  const getTitle = () => {
    let title = "";
    if (isActive) {
      title = "Deactivate Price Level?";
    } else {
      title = "Reactivate Price Level?";
    }
    if (hasAssignedAccounts) {
      title = "Unable to Deactivate Price Level";
    }
    return title;
  };

  return (
    <MiModal
      open={open}
      closeModal={closeModal}
      title={getTitle()}
      description={description}
      actions={actions}
    />
  );
}

interface DeactivateConfirmModalProps {
  open: boolean;
  selectedPriceLevel: IPriceLevelPayload | null;
  closeModal: () => void;
  handleUpdatePriceLevel: (
    data: Partial<IPriceLevelPayload>,
    successText: string
  ) => void;
}
